import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { apiUrl } from '../config';
import store from '../redux/store';
import { refresh } from '../modules/auth/actions';
import { selectIdToken } from '../modules/auth/selectors';

const client = axios.create({
  baseURL: apiUrl,
  timeout: 100000,
  headers: {
    'content-type': 'application/json',
  },
});

// Function to log HTTP errors to backend
const logErrorToBackend = async (error: AxiosError) => {
  try {
    if (!error.response || !error.config) {
      return;
    }

    const { status, statusText, data } = error.response;
    const { method, url, params, data: requestData } = error.config;

    let path = url || '';
    if (client.defaults.baseURL) {
      path = path.replace(client.defaults.baseURL, '');
    }

    const pathParts = path.split('?');
    const pathOnly = pathParts[0];
    let queryParams: Record<string, any> = params || {};

    if (pathParts.length > 1) {
      const queryString = pathParts[1];
      const urlParams = new URLSearchParams(queryString);
      urlParams.forEach((value, key) => {
        queryParams[key] = value;
      });
    }

    const logData = {
      statusCode: status,
      method: method?.toUpperCase() || 'UNKNOWN',
      path: pathOnly,
      errorName: error.name || 'HttpException',
      errorMessage:
        (data as any)?.message ||
        statusText ||
        error.message ||
        'Unknown error',
      stack: error.stack,
      requestBody: requestData,
      requestQuery: Object.keys(queryParams).length > 0 ? queryParams : null,
      requestParams: {},
      origin: 'Web',
    };

    const config: AxiosRequestConfig = {
      // @ts-ignore - Custom flag to prevent infinite loop
      __skipErrorLogging: true,
    };

    await client.post('admin/log-http-errors', logData, config).catch(() => {
      // Silently fail if logging fails
    });
  } catch (logError) {
    console.error('Error preparing log for backend:', logError);
  }
};

client.interceptors.request.use(
  async (config) => {
    const token = selectIdToken(store.getState());
    config.headers.authorization = `Bearer ${token}`;
    // Add origin header for backend to detect request source
    config.headers['x-request-origin'] = 'Web';
    return config;
  },
  (error) => Promise.reject(error),
);

client.interceptors.response.use(
  async (res) => {
    const refreshedToken = res.headers['x-refreshed-id-token'];

    if (refreshedToken) {
      store.dispatch(refresh({ jwt: refreshedToken }));
    }

    return res;
  },
  async (error: AxiosError) => {
    // Log error to backend (skip if this is a log request itself to prevent infinite loop)
    if (!(error.config as any)?.__skipErrorLogging) {
      await logErrorToBackend(error);
    }
    return Promise.reject(error);
  },
);

export default client;
